import React, { useState, useContext, useEffect, useCallback } from 'react';
import { UserContext } from '../userContext';
import { auth, db } from '../firebaseConfig';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaUpload, FaHistory, FaBell, FaSignOutAlt, FaChevronDown, FaHeart } from 'react-icons/fa';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import Notificacoes from '../components/Notificacoes';
import './UserMenu.css';

function UserMenu() {
  const { user, setUser } = useContext(UserContext);
  const userId = user?.uid;
  const [menuOpen, setMenuOpen] = useState(false);
  const [showNotificacoes, setShowNotificacoes] = useState(false);
  const [contadorNotificacoes, setContadorNotificacoes] = useState(0);
  const navigate = useNavigate();

  const carregarContadorNotificacoes = useCallback(async () => {
    if (!userId) return;

    try {
      const agora = Timestamp.now();
      
      // Buscar avisos ativos no período atual
      const q = query(
        collection(db, 'avisos'),
        where('ativo', '==', true),
        where('dataInicio', '<=', agora),
        where('dataFim', '>=', agora)
      );
      
      const snapshot = await getDocs(q);
      
      // Contar avisos não visualizados pelo usuário
      let naoLidos = 0;
      snapshot.docs.forEach(doc => {
        const aviso = doc.data();
        const visualizacoes = aviso.visualizacoes || [];
        if (!visualizacoes.includes(userId)) {
          naoLidos++;
        }
      });
      
      setContadorNotificacoes(naoLidos);
    } catch (error) {
      console.error('Erro ao carregar contador de notificações:', error);
    }
  }, [userId]);

  // Carregar contador de notificações
  useEffect(() => {
    if (!userId) return undefined;

    const runIfVisible = () => {
      if (document.visibilityState === 'visible') {
        carregarContadorNotificacoes();
      }
    };

    runIfVisible();
    // Reduz frequência para evitar excesso de consultas
    const interval = setInterval(runIfVisible, 120000);
    document.addEventListener('visibilitychange', runIfVisible);
    window.addEventListener('focus', runIfVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', runIfVisible);
      window.removeEventListener('focus', runIfVisible);
    };
  }, [userId, carregarContadorNotificacoes]);

  const handleLogout = async () => {
    await auth.signOut();
    setUser(null);
    setMenuOpen(false);
  };

  const handleMenuClick = (path) => {
    navigate(path);
    setMenuOpen(false);
  };

  const handleNotificacoesClick = () => {
    setShowNotificacoes(true);
    setMenuOpen(false);
  };

  const handleNotificacoesClose = () => {
    setShowNotificacoes(false);
    // Recarregar contador após fechar notificações
    setTimeout(() => {
      carregarContadorNotificacoes();
    }, 500);
  };

  const firstLetter = user?.name?.charAt(0).toUpperCase() || 'U';

  return (
    <div className="user-menu">
      <div 
        className="user-avatar-container" 
        onClick={() => setMenuOpen(!menuOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          cursor: 'pointer',
          padding: '0.5rem',
          borderRadius: '8px',
          transition: 'background 0.2s ease'
        }}
      >
        {/* Avatar */}
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          border: '2px solid #209cd3',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: user?.photoURL ? 'transparent' : '#209cd3',
          color: 'white',
          fontWeight: 'bold',
          fontSize: '1.2rem'
        }}>
          {user?.photoURL ? (
            <img 
              src={user.photoURL} 
              alt="Avatar" 
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          ) : (
            firstLetter
          )}
        </div>

        {/* Nome e seta */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem'
        }}>
          <span style={{
            fontSize: '0.9rem',
            fontWeight: '500',
            color: '#374151',
            maxWidth: '100px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {user?.name?.split(' ')[0] || 'Usuário'}
          </span>
          
          {/* Indicador de notificações */}
          {contadorNotificacoes > 0 && (
            <div style={{
              background: '#ef4444',
              color: 'white',
              fontSize: '0.7rem',
              fontWeight: '700',
              padding: '0.15rem 0.4rem',
              borderRadius: '10px',
              minWidth: '16px',
              height: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'pulse 2s ease-in-out infinite'
            }}>
              {contadorNotificacoes}
            </div>
          )}
          
          <FaChevronDown style={{
            fontSize: '0.7rem',
            color: '#6b7280',
            transition: 'transform 0.2s ease',
            transform: menuOpen ? 'rotate(180deg)' : 'rotate(0deg)'
          }} />
        </div>
      </div>

      {menuOpen && (
        <>
          {/* Overlay para fechar o menu */}
          <div
            onClick={() => setMenuOpen(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 99
            }}
          />
          
          {/* Menu dropdown */}
          <div className="dropdown-menu" style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '1rem',
            width: '320px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
            zIndex: 100,
            marginTop: '0.5rem'
          }}>
            {/* Header do usuário */}
            <div className="user-info" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '1rem',
              paddingBottom: '1rem',
              borderBottom: '1px solid #f1f5f9'
            }}>
              <div className="user-avatar-large" style={{
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                border: '2px solid #209cd3',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: user?.photoURL ? 'transparent' : '#209cd3',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1.4rem'
              }}>
                {user?.photoURL ? (
                  <img 
                    src={user.photoURL} 
                    alt="Avatar" 
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  firstLetter
                )}
              </div>
              
              <div className="user-details">
                <div style={{
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#1e293b',
                  marginBottom: '0.25rem'
                }}>
                  {user?.name}
                </div>
                <div style={{
                  fontSize: '0.85rem',
                  color: '#64748b',
                  marginBottom: '0.5rem'
                }}>
                  {user?.email}
                </div>
                <div className="user-plan" style={{
                  display: 'inline-block',
                  padding: '0.25rem 0.5rem',
                  background: user?.plano === 'Free' ? '#f1f5f9' : 'rgba(32, 156, 211, 0.1)',
                  color: user?.plano === 'Free' ? '#64748b' : '#209cd3',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}>
                  {user?.plano === 'Free' ? 'Gratuito' : user?.plano}
                </div>
              </div>
            </div>

            {/* Menu items */}
            <ul className="menu-list" style={{
              listStyle: 'none',
              padding: 0,
              margin: '0 0 1rem 0'
            }}>
              <li onClick={() => handleMenuClick('/perfil?tab=dados')} className="menu-item" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.75rem',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'background 0.2s ease',
                marginBottom: '0.5rem'
              }}>
                <FaUser className="menu-icon" style={{ color: '#64748b', fontSize: '1rem' }} /> 
                <div>
                  <div className="menu-label" style={{ fontWeight: '500', color: '#374151', fontSize: '0.9rem' }}>
                    Meus Dados
                  </div>
                  <div className="menu-description" style={{ fontSize: '0.8rem', color: '#64748b' }}>
                    Editar informações pessoais
                  </div>
                </div>
              </li>
              
              {/* ITEM: MEUS FAVORITOS */}
              <li onClick={() => handleMenuClick('/meus-favoritos')} className="menu-item favoritos-menu-item" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.75rem',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'background 0.2s ease',
                marginBottom: '0.5rem'
              }}>
                <FaHeart className="menu-icon favoritos-icon" style={{ color: '#e74c3c', fontSize: '1rem' }} /> 
                <div>
                  <div className="menu-label" style={{ fontWeight: '500', color: '#374151', fontSize: '0.9rem' }}>
                    Meus Favoritos
                  </div>
                  <div className="menu-description" style={{ fontSize: '0.8rem', color: '#64748b' }}>
                    Subcategorias e materiais salvos
                  </div>
                </div>
              </li>
              
              <li onClick={() => handleMenuClick('/perfil?tab=enviar')} className="menu-item" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.75rem',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'background 0.2s ease',
                marginBottom: '0.5rem'
              }}>
                <FaUpload className="menu-icon" style={{ color: '#64748b', fontSize: '1rem' }} /> 
                <div>
                  <div className="menu-label" style={{ fontWeight: '500', color: '#374151', fontSize: '0.9rem' }}>
                    Enviar Material
                  </div>
                  <div className="menu-description" style={{ fontSize: '0.8rem', color: '#64748b' }}>
                    Enviar dúvidas e exercícios
                  </div>
                </div>
              </li>
              
              <li onClick={() => handleMenuClick('/perfil?tab=envios')} className="menu-item" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.75rem',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'background 0.2s ease',
                marginBottom: '0.5rem'
              }}>
                <FaHistory className="menu-icon" style={{ color: '#64748b', fontSize: '1rem' }} /> 
                <div>
                  <div className="menu-label" style={{ fontWeight: '500', color: '#374151', fontSize: '0.9rem' }}>
                    Meus Envios
                  </div>
                  <div className="menu-description" style={{ fontSize: '0.8rem', color: '#64748b' }}>
                    Histórico de materiais enviados
                  </div>
                </div>
              </li>
              
              <li onClick={handleNotificacoesClick} className="menu-item notificacoes-menu-item" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.75rem',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'background 0.2s ease',
                marginBottom: '0.5rem'
              }}>
                <div style={{ position: 'relative' }}>
                  <FaBell className="menu-icon" style={{ color: '#64748b', fontSize: '1rem' }} />
                  {contadorNotificacoes > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '-4px',
                      right: '-4px',
                      background: '#ef4444',
                      color: 'white',
                      fontSize: '0.6rem',
                      fontWeight: '700',
                      padding: '0.1rem 0.3rem',
                      borderRadius: '8px',
                      minWidth: '14px',
                      height: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {contadorNotificacoes}
                    </div>
                  )}
                </div>
                <div>
                  <div className="menu-label" style={{ fontWeight: '500', color: '#374151', fontSize: '0.9rem' }}>
                    Notificações
                  </div>
                  <div className="menu-description" style={{ fontSize: '0.8rem', color: '#64748b' }}>
                    {contadorNotificacoes > 0 
                      ? `${contadorNotificacoes} nova${contadorNotificacoes > 1 ? 's' : ''}`
                      : 'Central de notificações'
                    }
                  </div>
                </div>
              </li>
            </ul>

            {/* Divisor */}
            <hr style={{
              height: '1px',
              background: '#f1f5f9',
              border: 'none',
              margin: '1rem 0'
            }} />

            {/* Logout */}
            <ul className="menu-list" style={{
              listStyle: 'none',
              padding: 0,
              margin: 0
            }}>
              <li onClick={handleLogout} className="logout-button menu-item" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.75rem',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'background 0.2s ease',
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                color: 'white'
              }}>
                <FaSignOutAlt className="menu-icon" style={{ color: 'white', fontSize: '1rem' }} /> 
                <div>
                  <div className="menu-label" style={{ color: 'white', fontWeight: '500', fontSize: '0.9rem' }}>
                    Sair
                  </div>
                  <div className="menu-description" style={{ color: '#fca5a5', fontSize: '0.8rem' }}>
                    Fazer logout da conta
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </>
      )}
      
      {/* Modal de Notificações */}
      {showNotificacoes && (
        <Notificacoes onClose={handleNotificacoesClose} />
      )}
    </div>
  );
}

export default UserMenu;
